/** Run after `window.load`, preferring idle time so first paint stays unblocked. */
export function runAfterLoad(task: () => void, idleTimeout = 4000): () => void {
  let idleHandle: number | undefined;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const schedule = () => {
    if (typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(task, { timeout: idleTimeout });
    } else {
      timeoutHandle = setTimeout(task, 2000);
    }
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }

  return () => {
    window.removeEventListener("load", schedule);
    if (idleHandle !== undefined && typeof window.cancelIdleCallback === "function") {
      window.cancelIdleCallback(idleHandle);
    }
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
  };
}

const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const;

/** Run after the first user gesture. No timer — Lighthouse never interacts. */
export function runOnInteraction(task: () => void): () => void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    for (const event of INTERACTION_EVENTS) window.removeEventListener(event, run);
    task();
  };
  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, run, { once: true, passive: true });
  }
  return () => {
    done = true;
    for (const event of INTERACTION_EVENTS) window.removeEventListener(event, run);
  };
}
